import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import ModalContainer from '../../components/modalContainer';
import AddBeneficiaryModal from '../../components/addBeneficiaryModal';
import UserCard from '../../components/userCard';
import DownloadBeneficiaries from '../../components/downloadBeneficiaries';
import ProjectLinkModal from '../../components/adminSchools/projectLinkModal';
import {
  getBeneficiariesByActivityGroup,
  getControlSocialActivityGroup,
  getList,
  updateControlSocialGroupProjectLink
} from '../../API/apiService';

function SocialControlGroup({
  activityId,
  groupId,
  initialActivity,
  initialGroup
}) {
  const location = useLocation();
  const [activity, setActivity] = useState(initialActivity || null);
  const [group, setGroup] = useState(initialGroup || null);
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [genders, setGenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [openModalAdd, setOpenModalAdd] = useState(false);
  const [openProjectLinkModal, setOpenProjectLinkModal] = useState(false);

  const currentBackTo = useMemo(() => ({
    pathname: location.pathname + location.search,
    state: location.state
  }), [location.pathname, location.search, location.state]);

  const loadBeneficiaries = useCallback(async () => {
    if (!groupId) {
      setBeneficiaries([]);
      return;
    }
    const list = await getBeneficiariesByActivityGroup(Number(groupId));
    setBeneficiaries(Array.isArray(list) ? list : []);
  }, [groupId]);

  useEffect(() => {
    let mounted = true;

    async function loadGroup() {
      setLoading(true);
      const result = await getControlSocialActivityGroup(
        Number(activityId),
        Number(groupId)
      );
      if (!mounted) return;

      if (result?.activity && result?.group) {
        setActivity(result.activity);
        setGroup(result.group);
        setNotFound(false);
      } else {
        setNotFound(true);
      }
      setLoading(false);
    }

    if (activityId && groupId) {
      loadGroup();
      loadBeneficiaries();
      getList('gender').then(list => {
        if (mounted && Array.isArray(list)) setGenders(list);
      });
    } else {
      setLoading(false);
      setNotFound(true);
    }

    return () => {
      mounted = false;
    };
  }, [activityId, groupId, loadBeneficiaries]);

  if (loading && !activity && !group) {
    return (
      <section className='beneficiaries beneficiaries--social-control'>
        <p className='beneficiaries__empty'>Cargando grupo...</p>
      </section>
    );
  }

  if (notFound || !activity || !group) {
    return (
      <section className='beneficiaries beneficiaries--social-control'>
        <p className='beneficiaries__empty'>
          No fue posible encontrar el grupo de Control Social.
        </p>
      </section>
    );
  }

  const meetingsPath =
    `/escuelas/grupo/lista_de_encuentros?controlSocial=true` +
    `&activityId=${activity.idActivity}&activityGroupId=${group.id}`;
  const navigationState = {
    controlSocialContext: true,
    activity,
    activityGroup: group,
    backTo: currentBackTo
  };
  const info = {activityGroup: group.id};
  const activeBeneficiaries = beneficiaries.filter(
    beneficiary => beneficiary?.active !== false
  );

  function handleBeneficiaryDeleted(deletedId) {
    setBeneficiaries(current =>
      current.filter(beneficiary => beneficiary?.id !== deletedId)
    );
    loadBeneficiaries();
  }

  return (
    <>
      {openModalAdd && (
        <ModalContainer>
          <AddBeneficiaryModal
            info={info}
            setOpenModalAdd={setOpenModalAdd}
            updateData={loadBeneficiaries}
            backTo={currentBackTo}
          />
        </ModalContainer>
      )}
      {openProjectLinkModal && (
        <ModalContainer>
          <ProjectLinkModal
            group={group}
            saveProjectLink={updateControlSocialGroupProjectLink}
            onClose={() => setOpenProjectLinkModal(false)}
            onSaved={updatedGroup => setGroup(updatedGroup)}
          />
        </ModalContainer>
      )}

      <section className='beneficiaries beneficiaries--social-control'>
        <div className='beneficiaries__main group'>
          <div className='group__info info'>
            <div className='info__icon icon'>
              <span
                className='material-symbols-rounded icon__img'
                aria-hidden='true'
              >
                groups
              </span>
            </div>
            <span className='info__title'>{activity.name}</span>
            <span className='info__value'>Grupo {group.code}</span>
          </div>

          <Link
            className='group__btn btn'
            to={meetingsPath}
            state={navigationState}
          >
            <span className='material-symbols-rounded btn__icon'>
              grid_view
            </span>
            <span className='btn__text'>Encuentros</span>
          </Link>

          <button
            className='group__btn btn'
            type='button'
            onClick={() => setOpenProjectLinkModal(true)}
          >
            <span className='material-symbols-rounded btn__icon'>link</span>
            <span className='btn__text'>Link proyecto</span>
          </button>

          <button
            className='group__btn btn'
            type='button'
            onClick={() => setOpenModalAdd(true)}
          >
            <span className='material-symbols-rounded btn__icon'>
              person_add
            </span>
            <span className='btn__text'>Agregar Beneficiario</span>
          </button>

          <DownloadBeneficiaries
            beneficiaries={activeBeneficiaries}
            genders={genders}
            buttonText='Descargar beneficiarios'
            buttonIcon='download'
            buttonClassName='group__btn btn group__btn--download'
            containerClassName='group__download'
            fileName={`beneficiarios_${group.code}.xlsx`}
            characterizationMode='social_control'
          />
        </div>

        <div className='beneficiaries__students'>
          {beneficiaries.length > 0 ? (
            beneficiaries.map((user, index) => (
              <UserCard
                key={user?.id || index}
                user={user}
                backTo={currentBackTo}
                onDeleted={handleBeneficiaryDeleted}
              />
            ))
          ) : (
            <p className='beneficiaries__empty'>
              Aun no hay beneficiarios registrados.
            </p>
          )}
        </div>
      </section>
    </>
  );
}

export default SocialControlGroup;
